/**
 * Effect Utilities for Reactive Signal Watching
 *
 * These utilities allow non-Lit classes (services, utilities) to react
 * to signal changes. Components should use SignalWatcher mixin instead.
 */

import { Signal } from "@lit-labs/signals";

type CleanupFn = () => void;

/**
 * Create a reactive effect that runs when accessed signals change.
 * Returns a cleanup function to stop the effect.
 *
 * The effect function runs immediately to track dependencies,
 * then re-runs whenever any accessed signal changes.
 *
 * @example
 * // In a service
 * class AnalyticsService {
 *   private cleanup?: CleanupFn;
 *
 *   initialize(): void {
 *     this.cleanup = effect(() => {
 *       const count = resolve("counterState").count;
 *       console.log("Count changed:", count);
 *       this.trackEvent("count_changed", count);
 *     });
 *   }
 *
 *   dispose(): void {
 *     this.cleanup?.();
 *   }
 * }
 */
export function effect(fn: () => void): CleanupFn {
  let scheduled = false;

  // Wrap fn in a Computed so the Watcher can track its signal dependencies.
  // Watcher.watch() only tracks explicitly passed signals — calling .get()
  // inside a plain function does not register dependencies with the Watcher.
  // A Computed acts as the tracking scope: it records which signals are read
  // during fn(), and the Watcher monitors the Computed for changes.
  const computed = new Signal.Computed(() => fn());

  const watcher = new Signal.subtle.Watcher(() => {
    if (!scheduled) {
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        computed.get();
        watcher.watch(computed);
      });
    }
  });

  // Initial run to track dependencies
  computed.get();
  watcher.watch(computed);

  return () => watcher.unwatch(computed);
}

/**
 * Watch a specific signal value and react to changes.
 * More explicit than effect() - you specify exactly what to watch.
 *
 * @param getter - Function that returns the value to watch
 * @param callback - Function called with the new value when it changes
 * @returns Cleanup function to stop watching
 *
 * @example
 * // Watch a specific value
 * const cleanup = watch(
 *   () => resolve("counterState").count,
 *   (newCount) => console.log("Count is now:", newCount)
 * );
 *
 * @example
 * // Watch derived value
 * const cleanup = watch(
 *   () => resolve("counterState").count > 10,
 *   (isHigh) => {
 *     if (isHigh) notifyUser("Count is high!");
 *   }
 * );
 */
export function watch<T>(getter: () => T, callback: (value: T) => void): CleanupFn {
  return effect(() => {
    const value = getter();
    callback(value);
  });
}
